import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {deleteLabelMutation} from '@github-ui/server-persisted-queries'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import type {LabelsQueryData} from './types'
import {updateLabelsCache} from './utils/update-labels-cache'

type UseDeleteLabelOptions = {
  labelId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setIsDialogOpen: (v: boolean) => void
  setErrorsPresent: (v: boolean) => void
}

type MutationContext = {
  previousData: LabelsQueryData | undefined
}

export function useDeleteLabel({
  labelId,
  repositoryNameWithOwner,
  queryKey,
  setIsDialogOpen,
  setErrorsPresent,
}: UseDeleteLabelOptions) {
  const queryClient = useQueryClient()

  const {mutate} = useMutation<void, Error, void, MutationContext>({
    mutationFn: async () => {
      let response: Response
      try {
        response = await verifiedFetchJSON(
          `/${repositoryNameWithOwner}/labels/${labelId}?queryId=${deleteLabelMutation}`,
          {
            method: 'DELETE',
          },
        )
      } catch {
        throw new Error('Delete failed')
      }

      if (!response.ok) {
        throw new Error('Delete failed')
      }
    },
    onMutate: async (): Promise<MutationContext> => {
      setIsDialogOpen(false)

      const previousData = queryKey ? queryClient.getQueryData<LabelsQueryData>(queryKey) : undefined

      // Optimistically remove the label from the TanStack cache
      if (queryKey) {
        queryClient.setQueryData<LabelsQueryData>(
          queryKey,
          updateLabelsCache(labels => {
            labels.totalCount = (labels.totalCount ?? 0) - 1
            labels.edges = labels.edges.filter(edge => edge?.node?.id !== labelId)
          }),
        )
      }

      return {previousData}
    },
    onError: (_error, _variables, context) => {
      if (queryKey && context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
      setErrorsPresent(true)
    },
  })

  return useCallback(() => {
    mutate()
  }, [mutate])
}
